import type { CSSProperties } from 'react';

/**
 * Props de fondo para <Container> a partir del campo opcional `FondoSeccion`
 * (color-picker de Strapi). Prioridad: color del input > clase fallback (toggle
 * existente del bloque) > nada (comportamiento por defecto). Se hace spread en
 * el Container: `<Container {...sectionBgProps(FondoSeccion, fallback)} />`.
 */
export const sectionBgProps = (
  fondoSeccion?: string,
  fallbackClass?: string,
): { backgroundColor?: string; style?: CSSProperties } => {
  if (fondoSeccion) return { style: { backgroundColor: fondoSeccion } };
  if (fallbackClass) return { backgroundColor: fallbackClass };
  return {};
};

/**
 * Devuelve `text-style-{slug}` para una entrada de `api::text-style`.
 * La clase la genera theme-config.js. Uso:
 *   <h1 className={textStyleClass(block.TituloEstilo) ?? 'text-headline …'}>
 */
export const textStyleClass = (textStyle: any): string | undefined => {
  if (!textStyle?.Slug) return undefined;
  return `text-style-${textStyle.Slug}`;
};

/**
 * Eyebrow específicamente: encadena per-block → global (theme-config) →
 * undefined. Los eyebrows visualmente no varían entre páginas/bloques
 * (a diferencia de títulos/descripciones), así que la mayoría de los
 * tenants sólo van a picar `eyebrowEstilo` en ThemeConfiguration y el
 * campo `EyebrowEstilo` por bloque queda como override puntual.
 *
 *   <span className={eyebrowClass(block.EyebrowEstilo) ?? 'hardcoded fallback'}>
 */
import { globalEyebrowEstiloClass } from '@/lib/contexts/global-text-styles.generated';
export const eyebrowClass = (textStyle: any): string | undefined =>
  textStyleClass(textStyle) ?? globalEyebrowEstiloClass ?? undefined;

// Nombres del enum Strapi `Weight` → número CSS. Los enums GraphQL no
// pueden empezar por dígito, por eso no usamos "400"/"500" directos.
const WEIGHT_NAMES: Record<string, number> = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
};

// Enum Strapi `Family` → CSS var donde theme-config.js puso la fuente del tenant.
const FAMILY_VARS: Record<string, string> = {
  primary: 'var(--font-family-primary)',
  secondary: 'var(--font-family-secondary)',
  accent: 'var(--font-family-accent)',
};

/**
 * Convierte una entrada de `api::text-style` en `CSSProperties` inline.
 * Sólo emite las props fijadas — el resto cae al estilo global del slot.
 * Devuelve `undefined` si no hay ningún valor (evita `style={}` vacío).
 */
export const buildTextStyle = (textStyle: any): CSSProperties | undefined => {
  if (!textStyle) return undefined;
  const style: CSSProperties = {};

  if (typeof textStyle.Size === 'number') {
    style.fontSize = `${textStyle.Size}px`;
  }
  if (typeof textStyle.Line === 'number') {
    style.lineHeight = `${textStyle.Line}px`;
  }
  const weight = WEIGHT_NAMES[textStyle.Weight];
  if (weight != null) {
    style.fontWeight = weight;
  }
  const family = FAMILY_VARS[textStyle.Family];
  if (family) {
    style.fontFamily = family;
  }
  if (typeof textStyle.LetterSpacing === 'number') {
    style.letterSpacing = `${textStyle.LetterSpacing}em`;
  }

  return Object.keys(style).length > 0 ? style : undefined;
};
